import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { GitBranch, ShieldAlert, CheckCircle2, XCircle, Layers } from "lucide-react";

const SCORING_GUIDE = [
  { bucket: "Hot Opportunity", tone: "bg-red-50 border-red-200",
    desc: "Strong CMMC/CUI need, clear deadline or contract trigger, missing SSP/POA&M or unclear CUI boundary, and decision-maker access.",
    action: "Call same day or within 24 hours." },
  { bucket: "Qualified Lead", tone: "bg-emerald-50 border-emerald-200",
    desc: "DoD activity and likely CMMC need, but needs more discovery.",
    action: "Send discovery call link or book call." },
  { bucket: "Education Lead", tone: "bg-blue-50 border-blue-200",
    desc: "Early-stage or unclear CUI need.",
    action: "Send CMMC overview and nurture." },
  { bucket: "Low Fit", tone: "bg-slate-50 border-slate-200",
    desc: "No DoD trigger, no CUI, no timeline, or no clear need.",
    action: "Do not spend active sales time." },
  { bucket: "Proposal Ready", tone: "bg-amber-50 border-amber-200",
    desc: "Discovery and intake confirm fit, business trigger is clear, and scope is understood.",
    action: "Prepare proposal and schedule walkthrough." },
  { bucket: "Proposal Sent", tone: "bg-violet-50 border-violet-200",
    desc: "Proposal delivered.",
    action: "Schedule proposal walkthrough within 24-48 hours." },
];

const OFFERS = [
  { name: "Tier 1: CMMC Diagnostic Assessment", when: "Prospect needs to understand current state, CUI scope, and roadmap before full readiness support.", label: "Diagnostic only. Not audit-ready." },
  { name: "Tier 2: Readiness + Documentation", when: "Needs SSP/POA&M documentation, CUI scoping, NIST SP 800-171 gap assessment, and a practical remediation roadmap.", label: "Most Common" },
  { name: "Tier 2.5: Readiness + GCC High / Cloud Alignment", when: "GCC High, Microsoft 365, Google Workspace, cloud migration, enclave design, or cloud alignment concerns tied to CMMC.", label: "" },
  { name: "Tier 3: Complex / Multi-System Readiness", when: "Larger, multi-system, hybrid, or complex CUI environments.", label: "" },
  { name: "Implementation Support", when: "Mainly technical remediation, tool configuration, Microsoft 365/GCC High deployment, endpoint security, or infrastructure implementation.", label: "" },
  { name: "Independent Assessment Path", when: "Prospect is asking about formal CMMC assessment. Make clear ComplyX Gov does not perform certification assessments.", label: "" },
];

export default function ScoringLogic() {
  const [guardrails, setGuardrails] = useState(null);
  useEffect(() => { api.get("/config").then((r) => setGuardrails(r.data.guardrails)); }, []);

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight leading-none flex items-center gap-2">
          <GitBranch className="h-6 w-6 text-gold" aria-hidden="true" /> Scoring Logic & Guardrails
        </h1>
      </header>

      <div className="p-6 md:p-8 space-y-8">
        <section>
          <h2 className="font-display text-lg font-bold text-navy mb-4">Lead Buckets</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SCORING_GUIDE.map((s) => (
              <div key={s.bucket} className={`rounded-md border p-5 ${s.tone}`}>
                <h3 className="font-display font-bold text-navy">{s.bucket}</h3>
                <p className="text-sm text-slate-700 mt-2 leading-relaxed">{s.desc}</p>
                <p className="text-xs text-navy font-medium mt-3 pt-3 border-t border-black/5">Next action: {s.action}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-display text-lg font-bold text-navy mb-4 flex items-center gap-2"><Layers className="h-5 w-5 text-gold-dark" /> Recommended Offers</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {OFFERS.map((o) => (
              <div key={o.name} className="rounded-md border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-bold text-navy">{o.name}</h3>
                  {o.label && <span className="text-[10px] uppercase tracking-wider bg-gold/20 text-gold-dark px-2 py-0.5 rounded shrink-0">{o.label}</span>}
                </div>
                <p className="text-sm text-slate-700 mt-2 leading-relaxed">{o.when}</p>
              </div>
            ))}
          </div>
        </section>

        {guardrails && (
          <section>
            <h2 className="font-display text-lg font-bold text-navy mb-4 flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-gold-dark" /> Compliance & Sales Guardrails</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-md border border-red-200 bg-red-50 p-5">
                <h3 className="font-semibold text-red-800 flex items-center gap-2 mb-3"><XCircle className="h-4 w-4" /> Do NOT say</h3>
                <ul className="space-y-2">
                  {guardrails.do_not_say.map((t) => <li key={t} className="text-sm text-red-700 flex items-center gap-2"><XCircle className="h-3.5 w-3.5 shrink-0" /> {t}</li>)}
                </ul>
              </div>
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-5">
                <h3 className="font-semibold text-emerald-800 flex items-center gap-2 mb-3"><CheckCircle2 className="h-4 w-4" /> Approved language</h3>
                <ul className="space-y-2">
                  {guardrails.use_instead.map((t) => <li key={t} className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> {t}</li>)}
                </ul>
              </div>
            </div>
            <div className="rounded-md border-l-4 border-l-gold bg-slate-50 border border-border p-5 mt-4">
              <p className="text-sm text-slate-700 leading-relaxed">{guardrails.disclaimer}</p>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
